import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const TutorialTaxDome = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const steps = [
    {
      number: 1,
      title: t('tutorial.step1.title'),
      description: t('tutorial.step1.desc'),
      details: [
        t('tutorial.step1.detail1'),
        t('tutorial.step1.detail2'),
        t('tutorial.step1.detail3'),
      ],
      image: 'https://public.readdy.ai/ai/img_res/e3a650aa30c676015e12285bee99d2aa.jpg',
    },
    {
      number: 2,
      title: t('tutorial.step2.title'),
      description: t('tutorial.step2.desc'),
      details: [
        t('tutorial.step2.detail1'),
        t('tutorial.step2.detail2'),
        t('tutorial.step2.detail3'),
      ],
      image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20dashboard%20interface%20showing%20client%20portal%20with%20organized%20menu%20sidebar%2C%20document%20folders%20and%20task%20list%2C%20professional%20accounting%20software%20design%2C%20teal%20and%20white%20color%20scheme%2C%20minimalist%20UI&width=600&height=400&seq=tut2&orientation=landscape',
    },
    {
      number: 3,
      title: t('tutorial.step3.title'),
      description: t('tutorial.step3.desc'),
      details: [
        t('tutorial.step3.detail1'),
        t('tutorial.step3.detail2'),
        t('tutorial.step3.detail3'),
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20file%20upload%20interface%20showing%20drag%20and%20drop%20zone%20with%20document%20icons%2C%20secure%20upload%20progress%20bar%2C%20cloud%20storage%20concept%2C%20professional%20business%20software%2C%20teal%20accent%20on%20white%20background%2C%20clean%20minimalist%20design&width=600&height=400&seq=tut3&orientation=landscape',
    },
    {
      number: 4,
      title: t('tutorial.step4.title'),
      description: t('tutorial.step4.desc'),
      details: [
        t('tutorial.step4.detail1'),
        t('tutorial.step4.detail2'),
        t('tutorial.step4.detail3'),
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20messaging%20interface%20showing%20secure%20chat%20conversation%20between%20client%20and%20accountant%2C%20professional%20communication%20platform%2C%20read%20receipts%20and%20timestamps%2C%20teal%20accent%20colors%2C%20clean%20white%20background%2C%20business%20software%20UI&width=600&height=400&seq=tut4&orientation=landscape',
    },
    {
      number: 5,
      title: t('tutorial.step5.title'),
      description: t('tutorial.step5.desc'),
      details: [
        t('tutorial.step5.detail1'),
        t('tutorial.step5.detail2'),
        t('tutorial.step5.detail3'),
      ],
      image: 'https://readdy.ai/api/search-image?query=Digital%20signature%20interface%20on%20tablet%20screen%20showing%20document%20signing%20process%20with%20signature%20field%20and%20confirmation%20button%2C%20secure%20e-signature%20platform%2C%20professional%20legal%20document%2C%20teal%20accents%20on%20white%2C%20modern%20clean%20design&width=600&height=400&seq=tut5&orientation=landscape',
    },
  ];

  const appFeatures = [
    {
      icon: 'ri-notification-3-line',
      title: t('tutorial.app.feature1'),
      desc: t('tutorial.app.feature1Desc'),
    },
    {
      icon: 'ri-camera-line',
      title: t('tutorial.app.feature2'),
      desc: t('tutorial.app.feature2Desc'),
    },
    {
      icon: 'ri-message-3-line',
      title: t('tutorial.app.feature3'),
      desc: t('tutorial.app.feature3Desc'),
    },
    {
      icon: 'ri-shield-check-line',
      title: t('tutorial.app.feature4'),
      desc: t('tutorial.app.feature4Desc'),
    },
  ];

  const faqs = [
    {
      question: t('tutorial.faq.q1'),
      answer: t('tutorial.faq.a1'),
    },
    {
      question: t('tutorial.faq.q2'),
      answer: t('tutorial.faq.a2'),
    },
    {
      question: t('tutorial.faq.q3'),
      answer: t('tutorial.faq.a3'),
    },
    {
      question: t('tutorial.faq.q4'),
      answer: t('tutorial.faq.a4'),
    },
    {
      question: t('tutorial.faq.q5'),
      answer: t('tutorial.faq.a5'),
    },
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95'}`}>
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/0e4a9c2d00ce897da69d164242b1c444/2b149d9c0dcc0f774da5ab5b579eeb2d.jpeg" alt="Amiraco Tax Service" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors font-medium cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center mr-1 inline-flex"><i className="ri-arrow-left-line"></i></span>
              {t('tutorial.backHome')}
            </Link>
            <a
              href="https://amiracotax.taxdome.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('tutorial.accessPortal')}
            </a>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shield-check-line"></i></span>
              {t('tutorial.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('tutorial.heroTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('tutorial.heroDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://amiracotax.taxdome.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-login-box-line"></i></span>
                {t('tutorial.loginNow')}
              </a>
              <a
                href="#download-app"
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-smartphone-line"></i></span>
                {t('tutorial.downloadApp')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why TaxDome Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tutorial.whyTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('tutorial.whyDesc')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="w-8 h-8 flex items-center justify-center text-white text-2xl"><i className="ri-lock-line"></i></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('tutorial.why1Title')}</h3>
              <p className="text-gray-600">{t('tutorial.why1Desc')}</p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="w-8 h-8 flex items-center justify-center text-white text-2xl"><i className="ri-time-line"></i></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('tutorial.why2Title')}</h3>
              <p className="text-gray-600">{t('tutorial.why2Desc')}</p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="w-8 h-8 flex items-center justify-center text-white text-2xl"><i className="ri-government-line"></i></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('tutorial.why3Title')}</h3>
              <p className="text-gray-600">{t('tutorial.why3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Tutorial */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tutorial.stepsTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('tutorial.stepsDesc')}</p>
          </div>

          {/* Step Navigation */}
          <div className="flex justify-center mb-12 overflow-x-auto pb-4">
            <div className="flex gap-2 bg-white p-2 rounded-full shadow-lg">
              {steps.map((step) => (
                <button
                  key={step.number}
                  onClick={() => setActiveStep(step.number)}
                  className={`w-12 h-12 rounded-full font-bold transition-all cursor-pointer whitespace-nowrap ${
                    activeStep === step.number
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {step.number}
                </button>
              ))}
            </div>
          </div>

          {/* Active Step Content */}
          {steps.map((step) => (
            <div
              key={step.number}
              className={`transition-all duration-500 ${
                activeStep === step.number ? 'block' : 'hidden'
              }`}
            >
              <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-medium mb-4 w-fit">
                      {t('tutorial.stepLabel')} {step.number}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 mb-6 text-lg">{step.description}</p>
                    <ul className="space-y-3">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-check-line"></i></span>
                          </span>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 lg:p-12 flex items-center justify-center">
                    <div className="w-full h-64 lg:h-80 rounded-2xl overflow-hidden shadow-lg">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Step Navigation Arrows */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
              disabled={activeStep === 1}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-teal-600 border-2 border-teal-600 hover:bg-teal-50'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              {t('tutorial.prevStep')}
            </button>
            <button
              onClick={() => setActiveStep(Math.min(5, activeStep + 1))}
              disabled={activeStep === 5}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeStep === 5
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              {t('tutorial.nextStep')}
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </button>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section id="download-app" className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <h2 className="text-3xl lg:text-4xl font-bold mb-6">{t('tutorial.appTitle')}</h2>
                <p className="text-teal-100 text-lg mb-8">{t('tutorial.appDesc')}</p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {appFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="w-5 h-5 flex items-center justify-center text-white"><i className={feature.icon}></i></span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{feature.title}</h4>
                        <p className="text-teal-100 text-sm">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://apps.apple.com/us/app/taxdome/id1472202357"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center text-3xl"><i className="ri-apple-fill"></i></span>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">{t('tutorial.downloadOn')}</div>
                      <div className="font-semibold">App Store</div>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.taxdome&hl=en_US&gl=US"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center text-3xl"><i className="ri-google-play-fill"></i></span>
                    <div className="text-left">
                      <div className="text-xs text-gray-400">{t('tutorial.getItOn')}</div>
                      <div className="font-semibold">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-72 h-[500px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Mobile%20app%20interface%20showing%20tax%20document%20management%20dashboard%20with%20clean%20modern%20design%2C%20notification%20badges%2C%20document%20upload%20button%2C%20secure%20messaging%20icon%2C%20teal%20accent%20colors%20on%20white%20background%2C%20professional%20accounting%20app%20UI&width=300&height=520&seq=tutapp&orientation=portrait"
                        alt="TaxDome App"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                    <span className="w-10 h-10 flex items-center justify-center text-teal-600 text-4xl"><i className="ri-shield-check-fill"></i></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tutorial.faqTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('tutorial.faqDesc')}</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <span className={`w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-teal-600"></i>
                  </span>
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === idx ? 'pb-5 max-h-96' : 'max-h-0'}`}>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('tutorial.ctaTitle')}</h2>
            <p className="text-gray-600 mb-8">{t('tutorial.ctaDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://amiracotax.taxdome.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-login-box-line"></i></span>
                {t('tutorial.accessPortal')}
              </a>
              <a
                href="https://wa.me/14153353243"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line"></i></span>
                {t('tutorial.needHelp')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="cursor-pointer">
              <img src="https://static.readdy.ai/image/0e4a9c2d00ce897da69d164242b1c444/2b149d9c0dcc0f774da5ab5b579eeb2d.jpeg" alt="Amiraco Tax Service" className="h-10 w-auto brightness-0 invert" />
            </Link>
            <p className="text-gray-400 text-sm">
              © 2024 Amiraco Tax Service. {t('footer.copyright')}
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 text-sm cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TutorialTaxDome;
